import { AiFeature, AiVersionsType } from '@tiendanube/common';
import { LanguagesType } from 'App/i18n/types';
import { logEvent } from 'commons/utils/tracking';
import {
  AI_GOOGLE_SHOPPING_CATEGORIES_USE_CLICK,
  AI_GENERATE_CANCEL_CLICK,
  AI_GENERATE_CONTENT_SELECT,
  AI_GENERATE_CTA_CLICK,
  AI_GENERATE_GENERATE_CLICK,
  AI_GENERATE_REGENERATE_CLICK,
  AI_GENERATE_USE_CONTENT_CLICK,
  USER_EDIT_NAME,
  USER_EDIT_DESCRIPTION,
  USER_EDIT_DEPTH,
  USER_EDIT_HEIGHT,
  USER_EDIT_WEIGHT,
  USER_EDIT_WIDTH,
  USER_EDIT_TAGS,
  USER_EDIT_BRAND,
  USER_EDIT_SEO_TITLE,
  USER_EDIT_SEO_DESCRIPTION,
  AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_COLOR_USE_CLICK,
  AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_IMAGE_USE_CLICK,
  AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_IMAGE_PICK_CLICK,
  AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_COLOR_PICK_CLICK,
  AI_EDIT_IMAGE_SMART_ERASE_CTA_CLICK,
  AI_EDIT_IMAGE_SMART_ERASE_ERASE_CLICK,
  AI_EDIT_IMAGE_SMART_ERASE_USE_CLICK,
  AI_EDIT_IMAGE_SMART_ERASE_UNDO_CLICK,
  AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_COLOR_CTA_CLICK,
  AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_IMAGE_CTA_CLICK,
  AI_PRODUCT_CATEGORY_REMOVE_CLICK,
  AI_PRODUCT_CATEGORY_FEEDBACK_POSITIVE_CLICK,
  AI_PRODUCT_CATEGORY_FEEDBACK_NEGATIVE_CLICK,
  AI_FEEDBACK_POSITIVE_CLICK,
  AI_FEEDBACK_NEGATIVE_CLICK,
} from 'config/trackingEvents';
import type { SizeField } from 'domains/Catalog/Products/pages/components/ProductSizes/ProductSizes';

interface TrackingAiVersionsProps {
  versions?: AiVersionsType;
}

export interface TrackingAiFeatureProps extends TrackingAiVersionsProps {
  feature?: AiFeature;
  language?: LanguagesType;
}

const logAiEvent = (
  eventName: string,
  trackingAiProps: TrackingAiFeatureProps,
  eventCustomProps?: Record<string, string>,
): void => {
  logEvent(eventName, {
    ...trackingAiProps.versions,
    ...eventCustomProps,
    ...(trackingAiProps.feature && { feature: trackingAiProps.feature }),
  });
};

// AI GOOGLE SHOPPING CATEGORIES
export const trackingAiGoogleShoppingCategoryUseClick = (
  position: number,
  trackingAiVersionsProps: TrackingAiVersionsProps,
): void => {
  logAiEvent(AI_GOOGLE_SHOPPING_CATEGORIES_USE_CLICK, trackingAiVersionsProps, {
    position: `${position}`,
  });
};

// AI GENERATION FEATURES
export const trackingAiGenerateCtaClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(AI_GENERATE_CTA_CLICK, trackingAiFeatureProps);
};

export const trackingAiGenerateGenerateClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(AI_GENERATE_GENERATE_CLICK, trackingAiFeatureProps);
};

export const trackingAiGenerateRegenerateClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
  attemptNumber: number,
  withoutInstruccions: boolean,
): void => {
  logAiEvent(AI_GENERATE_REGENERATE_CLICK, trackingAiFeatureProps, {
    attempt_number: `${attemptNumber}`,
    without_instruccions: withoutInstruccions ? 'true' : 'false',
  });
};

export const trackingAiGenerateCancelClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(AI_GENERATE_CANCEL_CLICK, trackingAiFeatureProps);
};

export const trackingAiGenerateUseContentClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
  eventCustomProps?: Record<string, string>,
): void => {
  logAiEvent(
    AI_GENERATE_USE_CONTENT_CLICK,
    trackingAiFeatureProps,
    eventCustomProps,
  );
};

export const trackingAiGenerateContentSelect = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(AI_GENERATE_CONTENT_SELECT, trackingAiFeatureProps);
};

export const trackingAiGenerateManualFieldEdit = (
  field: SizeField,
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  const eventMap: Record<SizeField, string> = {
    weight: USER_EDIT_WEIGHT,
    depth: USER_EDIT_DEPTH,
    width: USER_EDIT_WIDTH,
    height: USER_EDIT_HEIGHT,
  };

  logAiEvent(eventMap[field], trackingAiFeatureProps);
};

export const trackingAiGenerateTagsEdit = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(USER_EDIT_TAGS, trackingAiFeatureProps);
};

export const trackingAiGenerateBrandEdit = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(USER_EDIT_BRAND, trackingAiFeatureProps);
};

export const trackingAiGenerateSeoTitleEdit = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
  language: string,
): void => {
  logAiEvent(USER_EDIT_SEO_TITLE, trackingAiFeatureProps, { language });
};

export const trackingAiGenerateSeoDescriptionEdit = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
  language: string,
): void => {
  logAiEvent(USER_EDIT_SEO_DESCRIPTION, trackingAiFeatureProps, { language });
};

// AI IMAGE FEATURE REPLACE BACKGROUND - COLOR
export const trackingAiEditImageReplaceBgWithColorCtaClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(
    AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_COLOR_CTA_CLICK,
    trackingAiFeatureProps,
  );
};

export const trackingAiEditImageReplaceBgWithColorPickClick = (
  color: string,
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(
    AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_COLOR_PICK_CLICK,
    trackingAiFeatureProps,
    {
      color,
    },
  );
};

export const trackingAiEditImageReplaceBgWithColorUseClick = (
  color: string,
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(
    AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_COLOR_USE_CLICK,
    trackingAiFeatureProps,
    {
      color,
    },
  );
};

// AI IMAGE FEATURE REPLACE BACKGROUND - IMAGE
export const trackingAiEditImageReplaceBgWithImageCtaClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(
    AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_IMAGE_CTA_CLICK,
    trackingAiFeatureProps,
  );
};

export const trackingAiEditImageReplaceBgWithImagePickClick = (
  selectedImageId: string,
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(
    AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_IMAGE_PICK_CLICK,
    trackingAiFeatureProps,
    {
      selected_image_id: selectedImageId,
    },
  );
};

export const trackingAiEditImageReplaceBgWithImageUseClick = (
  selectedImageId: string,
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(
    AI_EDIT_IMAGE_REPLACE_BACKGROUND_WITH_IMAGE_USE_CLICK,
    trackingAiFeatureProps,
    {
      selected_image_id: selectedImageId,
    },
  );
};

// AI IMAGE FEATURE SMART ERASE
export const trackingAiEditImageSmartEraseCtaClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(AI_EDIT_IMAGE_SMART_ERASE_CTA_CLICK, trackingAiFeatureProps);
};

export const trackingAiEditImageSmartEraseEraseClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(AI_EDIT_IMAGE_SMART_ERASE_ERASE_CLICK, trackingAiFeatureProps);
};

export const trackingAiEditImageSmartEraseUndoClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(AI_EDIT_IMAGE_SMART_ERASE_UNDO_CLICK, trackingAiFeatureProps);
};

export const trackingAiEditImageSmartEraseUseClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps = {},
): void => {
  logAiEvent(AI_EDIT_IMAGE_SMART_ERASE_USE_CLICK, trackingAiFeatureProps);
};

// AI PRODUCT CATEGORY SUGGESTIONS
export const trackingAiProductCategoryRemoveClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(AI_PRODUCT_CATEGORY_REMOVE_CLICK, trackingAiFeatureProps);
};

export const trackingAiProductCategoryFeedbackPositiveClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(
    AI_PRODUCT_CATEGORY_FEEDBACK_POSITIVE_CLICK,
    trackingAiFeatureProps,
  );
};

export const trackingAiProductCategoryFeedbackNegativeClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(
    AI_PRODUCT_CATEGORY_FEEDBACK_NEGATIVE_CLICK,
    trackingAiFeatureProps,
  );
};

// Generic AI Feedback Tracking
export const trackingAiFeedbackPositiveClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(AI_FEEDBACK_POSITIVE_CLICK, trackingAiFeatureProps);
};

export const trackingAiFeedbackNegativeClick = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
): void => {
  logAiEvent(AI_FEEDBACK_NEGATIVE_CLICK, trackingAiFeatureProps);
};

export const trackingAiProductNameEdit = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
  language: string,
): void => {
  logAiEvent(USER_EDIT_NAME, trackingAiFeatureProps, {
    language,
  });
};

export const trackingAiProductDescriptionEdit = (
  trackingAiFeatureProps: TrackingAiFeatureProps,
  language: string,
): void => {
  logAiEvent(USER_EDIT_DESCRIPTION, trackingAiFeatureProps, {
    language,
  });
};
